import { Color } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import React, { useCallback, useEffect, useState } from 'react';
import { Animated, Easing } from 'react-native';

import { SignedInNavigationScreenNavigationProp } from '../../../../../../routes/types';
import { CustomersListResponseNormalized } from '../../../../../../store/blockList/selector';

import { RestrictedCustomersListItemDesktop } from './Desktop';
import { RestrictedCustomersListItemMobile } from './Mobile';
import { RestrictedCustomersListItemTablet } from './Tablet';

export declare namespace RestrictedCustomersListItemTypes {
  export interface Props {
    item: CustomersListResponseNormalized;
    sendRetryUnlockCustomer: ({
      customerId,
      channel,
      optoutId,
    }: {
      customerId: number;
      channel: string;
      optoutId: string;
    }) => void;
    blockListRetryLoading: boolean;
    currentOptoutId: string;
  }

  export interface Content {
    rotation: Animated.Value;
    blockListRetryLoading: boolean;
    item: CustomersListResponseNormalized;
    isSendDisabled: boolean;
    actionIconColor: string;
    actionTextColor: string;
    actionPressable: () => void;
  }
}

export const RestrictedCustomersListItem: React.FC<RestrictedCustomersListItemTypes.Props> = ({
  blockListRetryLoading,
  item,
  sendRetryUnlockCustomer,
  currentOptoutId,
}) => {
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();

  const isSendDisabled = item.canRetry ? false : item.statusName !== 'Blocked';
  const actionIconColor = item.canRetry
    ? Color['brand.tertiary.principal']
    : item.statusName !== 'Blocked'
    ? Color['neutral.light']
    : Color['brand.tertiary.principal'];

  const actionTextColor = item.canRetry
    ? 'brand.tertiary.principal'
    : item.statusName !== 'Blocked'
    ? 'neutral.light'
    : 'brand.tertiary.principal';

  const actionPressable = () => {
    if (item.canRetry) {
      sendRetryUnlockCustomer({
        customerId: parseInt(item.requestedUser!, 10),
        channel: item.channel!,
        optoutId: item.optoutId!,
      });
    } else {
      navigate('UnlockCustomer', {
        customerId: item.requestedUser!,
        optoutId: item.optoutId!,
      });
    }
  };

  const [rotation] = useState(new Animated.Value(0));

  const rotateAnimation = useCallback(() => {
    Animated.timing(rotation, {
      toValue: 1,
      duration: 1000,
      easing: Easing.linear,
      useNativeDriver: true,
    }).start(() => {
      rotation.setValue(0);
      rotateAnimation();
    });
  }, [rotation]);

  useEffect(() => {
    if (blockListRetryLoading && currentOptoutId === item.optoutId) {
      rotateAnimation();
    } else {
      rotation.setValue(0);
    }
  }, [blockListRetryLoading, currentOptoutId, item.optoutId, rotateAnimation, rotation]);

  const content = useBreakpointValue({
    base: (
      <RestrictedCustomersListItemMobile
        rotation={rotation}
        blockListRetryLoading={blockListRetryLoading}
        item={item}
        actionPressable={actionPressable}
        isSendDisabled={isSendDisabled}
        actionIconColor={actionIconColor}
        actionTextColor={actionTextColor}
      />
    ),
    sm: (
      <RestrictedCustomersListItemTablet
        rotation={rotation}
        blockListRetryLoading={blockListRetryLoading}
        item={item}
        actionPressable={actionPressable}
        isSendDisabled={isSendDisabled}
        actionIconColor={actionIconColor}
        actionTextColor={actionTextColor}
      />
    ),
    md: (
      <RestrictedCustomersListItemDesktop
        rotation={rotation}
        blockListRetryLoading={blockListRetryLoading}
        item={item}
        actionPressable={actionPressable}
        isSendDisabled={isSendDisabled}
        actionIconColor={actionIconColor}
        actionTextColor={actionTextColor}
      />
    ),
  });

  return content;
};
